import { Theme } from "@mui/material/styles";

import { UI_SIZES } from "src/constants/uiTokens";

const defaultHeight = `${UI_SIZES.inputHeight}px`;
const iconSize = `${UI_SIZES.iconButtonSize}px`;

/**
 * Surface tokens these helpers are built from. `theme` is optional and
 * defaults to the light-mode literals below — every call site that doesn't
 * pass a theme yet keeps its current appearance unchanged. Passing the
 * current theme (e.g. `useTheme()`) makes the returned styles dark-mode aware.
 */
const getSurfaceTokens = (theme?: Theme) => {
  if (theme?.palette.mode !== "dark") {
    return {
      surface: "#FFF",
      surfaceHover: "#EBEBEB",
      onSurface: "#000",
      outline: "#000",
      outlineMuted: "#00000043",
      inputBackground: "#E5E9ED",
      pageWash: "#E5E9ED70",
      // Text/select/date input field outline. Same #5B6D7A as light mode's
      // `outline` above -- kept as its own token since inputs use it in
      // more places (fieldset, notchedOutline, plain border) than the
      // handful of components that reference `outline` directly.
      inputBorder: "#5B6D7A",
      // #202338 doubles as this token's own dark-mode value below, so in
      // light mode a bare "elevated" chip/background just uses it directly.
      elevatedSurface: "#202338",
      // Muted label/icon color for secondary text -- flips to white in dark
      // mode since #5B6D7A reads as near-invisible against a dark page.
      mutedText: "#5B6D7A",
      // Standard row/panel divider border.
      dividerBorder: "1px solid #5B6D7A50",
      // Settings "description" sub-panel background (e.g. exam package /
      // lens option detail views).
      descriptionBackground: "#E5E9ED",
      // Hover partner for descriptionBackground -- base is now fully
      // opaque, so a higher-opacity trick can't read as "darker" anymore;
      // this is a solid, one-step-darker shade of the same blue-grey hue
      // instead (same idea as surfaceHover sitting one step below surface),
      // rather than jumping to surfaceHover's warmer neutral grey.
      descriptionBackgroundHover: "#D6DCE1",
      // Disabled partner -- same blue-grey hue at low opacity (matching the
      // ~26% "43" alpha getDarkSolidColors uses for its own disabled state),
      // so a disabled surface visibly fades toward the page instead of
      // looking identical to the interactive base.
      descriptionBackgroundDisabled: "#E5E9ED43",
    };
  }

  return {
    surface: theme.palette.background.paper,
    surfaceHover: "#2A2E44",
    onSurface: theme.palette.text.primary,
    outline: "#5B6D7A80",
    outlineMuted: theme.palette.divider,
    inputBackground: "#2A2E44",
    pageWash: theme.palette.background.default,
    // Brighter than the plain #5B6D7A used everywhere else, so input field
    // outlines stay visible against the dark input background.
    inputBorder: "#8B94A1",
    // In dark mode #202338 is the page's own paper color, so an "elevated"
    // chip/background is lightened a step to still read as distinct --
    // same reasoning as getDarkSolidColors below.
    elevatedSurface: "#4D5063",
    mutedText: "#FFF",
    dividerBorder: "1px solid #5B6D7A",
    descriptionBackground: "#202338",
    // Same "one elevation step up" used for elevatedSurface's dark literal
    // below -- #202338 is the page's own paper color in dark mode, so
    // hovering it lightens to the next step rather than darkening further.
    descriptionBackgroundHover: "#4D5063",
    // Same ~26% "43" alpha getDarkSolidColors uses for its own disabled
    // state, applied to this token's own base color.
    descriptionBackgroundDisabled: "#20233843",
  };
};

// ---------------------------------------------------------------------------
// Solid Button / IconButton variant factory
//
// Every "solid color" Button and IconButton style below (confirm, cancel,
// delete, warning, grey, dark, ...) is really just three color states --
// base, hover, disabled -- laid over the same handful of structural
// properties. Building a new variant means supplying those three color
// states to buildButtonSx/buildIconButtonSx instead of hand-copying the
// full ~25-line object (and its hover/disabled boilerplate) from an
// existing one.
// ---------------------------------------------------------------------------

type ColorState = {
  color?: string;
  backgroundColor?: string;
  border?: string;
};

type SolidVariantColors = {
  base: ColorState;
  hover?: ColorState;
  disabled?: ColorState;
};

const BUTTON_BASE_SX = {
  height: defaultHeight,
  minWidth: "100px",
  fontSize: "14px",
  padding: "0px 20px",
  borderRadius: "4px",
  "& .MuiButton-startIcon svg": {
    width: "16px",
    height: "16px",
  },
  "& .MuiButton-endIcon svg": {
    width: "16px",
    height: "16px",
  },
};

/**
 * Builds a solid-color Button variant's sx object from its base/hover/
 * disabled color states. `overrides` can tweak non-color structural
 * properties (e.g. padding) for a variant that isn't shaped exactly like
 * the rest -- see ModalCustomConfirmLink.
 */
const buildButtonSx = (
  { base, hover, disabled }: SolidVariantColors,
  overrides: Record<string, unknown> = {}
) => ({
  ...BUTTON_BASE_SX,
  ...base,
  ...overrides,
  "&:hover": { ...base, ...hover },
  "&.Mui-disabled": { ...base, ...disabled },
});

const ICON_BUTTON_BASE_SX = {
  borderRadius: "4px",
  width: iconSize,
  height: iconSize,
};

/**
 * IconButton counterpart to buildButtonSx -- same base/hover/disabled color
 * states, sized for an icon-only button instead.
 */
const buildIconButtonSx = ({ base, hover, disabled }: SolidVariantColors) => ({
  ...ICON_BUTTON_BASE_SX,
  ...base,
  "&:hover": { ...base, ...hover },
  "&:disabled": { ...base, ...disabled },
});

/**
 * Color states shared by ModalCustomConfirmDark and CustomIconButtonDark.
 * `theme` is required (not optional) here -- unlike getSurfaceTokens, whose
 * optional-with-light-default is a deliberate, documented API for
 * intentionally-light contexts, this pattern has no legitimate "call it
 * with no theme" use case. Making it required turns a forgotten theme into
 * a compile error instead of a silently-wrong dark-mode color.
 */
const getDarkSolidColors = (theme: Theme): SolidVariantColors => {
  const isDark = theme.palette.mode === "dark";
  const { elevatedSurface } = getSurfaceTokens(theme);

  const backgroundHover = isDark ? "#5C6076" : "#0C0F24";
  const backgroundDisabled = isDark ? "#4D506343" : "#20233843";

  return {
    base: { color: "#FFF", backgroundColor: elevatedSurface, border: "none" },
    hover: { backgroundColor: backgroundHover },
    disabled: { backgroundColor: backgroundDisabled },
  };
};

/**
 * Shared Menu/Popover Paper styling -- a plain surface background with the
 * default MUI paper gradient stripped out, plus a border that only shows up
 * in dark mode (light mode relies on the drop shadow alone to read as
 * "floating"). Pass straight to `PaperProps.sx` / `slotProps.paper.sx`.
 */
const getPopoverPaperSx = (theme?: Theme) => {
  const { surface, elevatedSurface } = getSurfaceTokens(theme);

  return {
    backgroundColor: surface,
    backgroundImage: "none",
    border:
      theme?.palette.mode === "dark" ? `1px solid ${elevatedSurface}` : "none",
  };
};

/**
 * Hover/selected background colors for menu-style dropdown items (used by
 * CustomAutocomplete, SearchBox/ClinicSwitcher, and SelectDropdown). Dark
 * mode uses a more opaque tint than light mode -- a low-alpha overlay barely
 * registers against the app's dark surface colors, so hover swaps to the
 * same solid `surfaceHover` used elsewhere for interactive-element hover,
 * and selected gets a stronger alpha to stay visibly distinct from it.
 */
const getMenuItemStateColors = (theme?: Theme) => {
  const { surfaceHover } = getSurfaceTokens(theme);
  const isDark = theme?.palette.mode === "dark";

  return {
    selected: isDark ? "#E2772E40" : "#E2772E20",
    hover: isDark ? surfaceHover : "#5B6D7A20",
  };
};

/**
 * Styles for a TextField element.
 */
const CustomTextFieldProps = (theme?: Theme) => {
  const { inputBackground, onSurface } = getSurfaceTokens(theme);

  return {
    backgroundColor: inputBackground,
    color: onSurface,
    borderRadius: "4px",
    height: defaultHeight,
  };
};

/**
 * Styles for a TextField element.
 */
const CustomMultiLineTextFieldProps = (theme?: Theme) => {
  const { inputBackground, onSurface } = getSurfaceTokens(theme);

  return {
    backgroundColor: inputBackground,
    color: onSurface,
    borderRadius: "4px",
  };
};

/**
 * Styles for a modal's cancel Button.
 */
const ModalCustomCancel = (theme?: Theme) => {
  const { surface, surfaceHover, onSurface, outline, outlineMuted } =
    getSurfaceTokens(theme);
  const borderColor = theme?.palette.mode === "dark" ? "#5B6D7A" : outline;

  return buildButtonSx({
    base: {
      color: onSurface,
      backgroundColor: surface,
      border: `1px solid ${borderColor}`,
    },
    hover: { backgroundColor: surfaceHover },
    disabled: {
      color: outlineMuted,
      border: `1px solid ${outlineMuted}`,
    },
  });
};

/**
 * Styles for a modal's delete Button.
 */
const ModalCustomDelete = (theme?: Theme) => {
  const { surface, surfaceHover } = getSurfaceTokens(theme);

  return buildButtonSx({
    base: {
      color: "#E10C0C",
      backgroundColor: surface,
      border: "1px solid #E10C0C",
    },
    hover: { backgroundColor: surfaceHover },
    disabled: { color: "#E10C0C43", border: "1px solid #E10C0C43" },
  });
};

/**
 * Styles for a modal's confirm Button. A true "link" style -- no
 * background/border in any state, so it never boxes itself off from
 * whatever surface it's placed on.
 */
const ModalCustomConfirmLink = () =>
  buildButtonSx(
    {
      base: { color: "#E2772E", backgroundColor: "transparent" },
      hover: {
        color: "#CE631A",
        backgroundColor: "transparent",
        border: "none",
      },
      disabled: {
        color: "#E2772E60",
        backgroundColor: "transparent",
        border: "none",
      },
    },
    { padding: "0px" }
  );

const ModalCustomDarkLink = () =>
  buildButtonSx(
    {
      base: { color: "#000000", backgroundColor: "transparent" },
      hover: {
        color: "#000000",
        backgroundColor: "transparent",
        border: "none",
      },
      disabled: {
        color: "#00000060",
        backgroundColor: "transparent",
        border: "none",
      },
    },
    { padding: "0px" }
  );

/**
 * Styles for a modal's confirm Button.
 */
const ModalCustomConfirm = () =>
  buildButtonSx({
    base: { color: "#FFF", backgroundColor: "#E2772E", border: "none" },
    hover: { backgroundColor: "#CE631A" },
    disabled: { backgroundColor: "#E2772E60" },
  });

/**
 * Styles for a modal's confirm Button (teal variant) -- matches the
 * EMR pages' ACCENT_TEAL.
 */
const ModalCustomConfirmTeal = () =>
  buildButtonSx({
    base: { color: "#FFF", backgroundColor: "#1B8F76", border: "none" },
    hover: { backgroundColor: "#15705D" },
    disabled: { backgroundColor: "#1B8F7660" },
  });

/**
 * Link-style Button in the page's own onSurface color (white in dark mode,
 * black in light mode) -- for a "back" link that should read as plain
 * foreground text rather than an accent color. `theme` is required, same
 * reasoning as getDarkSolidColors: a forgotten theme here would otherwise
 * silently fall back to the light-mode color in dark mode.
 */
const ModalCustomSurfaceLink = (theme: Theme) => {
  const { onSurface } = getSurfaceTokens(theme);

  return buildButtonSx(
    {
      base: { color: onSurface, backgroundColor: "transparent" },
      hover: {
        color: onSurface,
        backgroundColor: "transparent",
        border: "none",
      },
      disabled: {
        color: `${onSurface}60`,
        backgroundColor: "transparent",
        border: "none",
      },
    },
    { padding: "0px", textDecoration: "underline" }
  );
};

/**
 * Styles for a modal's confirm Button (dark variant).
 */
const ModalCustomConfirmDark = (theme: Theme) =>
  buildButtonSx(getDarkSolidColors(theme));

/**
 * ModalCustomConfirmDark, but orange instead of navy in dark mode -- on a
 * few dark surfaces (e.g. a modal's own body) the navy blends in too
 * closely to read as a distinct button, so this swaps in the same orange
 * CTA color used elsewhere in the app for real contrast.
 */
const ModalCustomConfirmDarkCta = (theme: Theme) => ({
  ...ModalCustomConfirmDark(theme),
  ...(theme.palette.mode === "dark" && {
    backgroundColor: "#E2772E",
    "&:hover": { backgroundColor: "#CE631A" },
  }),
});

/**
 * Styles for the Exam/Simulation/Video "picker" buttons (SelectExamButton,
 * SelectSimulationButton, SelectVideoButton) that add an item to the
 * selected list. Deliberately not theme-reactive like ModalCustomConfirmDark
 * -- both states should look like their light-mode selves in both modes,
 * `theme` is only read to pick the unselected background: the literal
 * light-mode grey (descriptionBackground) reads as too washed-out/bright
 * against an actual dark-mode page, so dark mode gets its own, deliberately
 * darker "light grey" shade instead.
 */
/**
 * Dark-mode "light chip" color set shared by ExamSliderButton's unselected
 * state and CustomIconButtonLight -- both should read as the same family
 * rather than each inventing its own shade. text/textDisabled come from
 * getSurfaceTokens' light-mode literal (not the real theme's dark-mode
 * onSurface), since these background colors are light enough to need dark
 * text regardless of which mode the page is actually in.
 */
const getExamSliderButtonColors = () => {
  const { onSurface } = getSurfaceTokens({
    palette: { mode: "light" },
  } as Theme);

  return {
    background: "#9DA5C2",
    backgroundHover: "#878FAC",
    backgroundDisabled: "#9DA5C280",
    text: onSurface,
    textDisabled: `${onSurface}80`,
  };
};

const ExamSliderButton = (theme: Theme, isChecked: boolean) => {
  const lightModeTheme = { palette: { mode: "light" } } as Theme;

  if (isChecked) {
    return { ...ModalCustomConfirmDark(lightModeTheme), padding: "0px 16px" };
  }

  const isDarkMode = theme.palette.mode === "dark";
  const {
    onSurface,
    descriptionBackground,
    descriptionBackgroundHover,
    descriptionBackgroundDisabled,
  } = getSurfaceTokens(lightModeTheme);
  const darkColors = getExamSliderButtonColors();

  const unselectedBackground = isDarkMode
    ? darkColors.background
    : descriptionBackground;
  const unselectedBackgroundHover = isDarkMode
    ? darkColors.backgroundHover
    : descriptionBackgroundHover;
  const unselectedBackgroundDisabled = isDarkMode
    ? darkColors.backgroundDisabled
    : descriptionBackgroundDisabled;

  return {
    height: "35px",
    minWidth: "100px",
    fontSize: "14px",
    padding: "0px 16px",
    borderRadius: "4px",
    color: onSurface,
    backgroundColor: unselectedBackground,
    border: "none",
    "&:hover": { backgroundColor: unselectedBackgroundHover },
    "&.Mui-disabled": {
      color: `${onSurface}80`,
      backgroundColor: unselectedBackgroundDisabled,
    },
  };
};

/**
 * Styles for a modal's confirm Button (grey variant).
 */
const ModalCustomConfirmGrey = () =>
  buildButtonSx({
    base: {
      color: "#FFF",
      backgroundColor: "#5B6D7A",
      border: "1px solid #5B6D7A",
    },
    hover: { backgroundColor: "#475966", border: "1px solid #475966" },
    disabled: { backgroundColor: "#5B6D7A60", border: "1px solid #5B6D7A60" },
  });

/**
 * Styles for a modal's confirm Button (grey variant, outlined).
 */
const ModalCustomConfirmGreyOutlined = (theme?: Theme) => {
  const { surface } = getSurfaceTokens(theme);

  return buildButtonSx({
    base: {
      color: "#5B6D7A",
      backgroundColor: surface,
      border: "1px solid #5B6D7A",
    },
    hover: { backgroundColor: "#5B6D7A20", border: "1px solid #5B6D7A" },
    disabled: { color: "#5B6D7A60", border: "1px solid #5B6D7A60" },
  });
};

/**
 * Styles for a modal's confirm Button (black variant). Required `theme` for
 * the same reason as ModalCustomConfirmDark -- see getDarkSolidColors.
 */
const ModalCustomConfirmBlack = (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";

  // A solid black CTA disappears against a dark page — flip to a light
  // surface in dark mode so it keeps the same "strongest button" contrast.
  const background = isDark ? "#F5F6F8" : "#000";
  const color = isDark ? "#12141C" : "#FFF";

  return buildButtonSx({
    base: { color, backgroundColor: background, border: "none" },
    // Same muted grey reads fine on both the dark button (light mode) and
    // the light button (dark mode); background stays put on hover.
    hover: { color: "#5B6D7A" },
    disabled: { backgroundColor: "#5B6D7A70" },
  });
};

/**
 * Styles for a modal's confirm Button (solid error variant).
 */
const ModalCustomDeleteSolid = () =>
  buildButtonSx({
    base: {
      color: "#FFF",
      backgroundColor: "#E10C0C",
      border: "1px solid #E10C0C",
    },
    hover: { backgroundColor: "#BF0808" },
    disabled: {
      backgroundColor: "#E10C0C43",
      border: "1px solid #E10C0C43 !important",
    },
  });

/**
 * Styles for a modal's warning Button.
 */
const ModalCustomWarning = (theme?: Theme) => {
  const { surface } = getSurfaceTokens(theme);

  return buildButtonSx({
    base: {
      color: "#E7C43C",
      backgroundColor: surface,
      border: "1px solid #E7C43C",
    },
    hover: { color: "#D3B028", border: "1px solid #D3B028" },
    disabled: { color: "#E7C43C43", border: "1px solid #E7C43C43" },
  });
};

/**
 * Styles for a modal's warning Button (solid variant).
 */
const ModalCustomWarningSolid = () =>
  buildButtonSx({
    base: { color: "#000", backgroundColor: "#E7C43C", border: "none" },
    hover: { backgroundColor: "#D3B028" },
    disabled: { backgroundColor: "#E7C43C43" },
  });

/**
 * Used to apply some default styling to the page wrapper container.
 */
const PageContainerWrapper = (
  theme?: Theme,
  custom = { minHeight: "100%", flexGrow: 1 }
) => {
  const { pageWash } = getSurfaceTokens(theme);

  return {
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    backgroundColor: pageWash,
    padding: "16px",
    width: "100%",
    height: "100%",
    ...custom,
  };
};

/**
 * Diagonal brand-color wash used behind the Emr page's tabs/header. Light
 * mode blends the accent colors (at low alpha) over a near-white base, which
 * reads as a pastel tint. Dark mode keeps that same wash but noticeably
 * dimmer/darker overall: the overlay's alpha is lower (35% -> 25%) so the
 * brand colors read as a subtle glow instead of dominating the page, and the
 * base fades from the theme's own dark `background.default` token down to a
 * deeper near-black rather than up to the (comparatively light) `paper`
 * token, so the page reads as dark first, tinted second.
 */
const getEmrPageBackground = (theme?: Theme) => {
  const isDark = theme?.palette.mode === "dark";

  const overlay = isDark
    ? "linear-gradient(102deg,#9B7CC040 -17.62%,#7A85C040 30.87%,#588EC040 87.31%)"
    : "linear-gradient(102deg,#9B7CC059 -17.62%,#7A85C059 30.87%,#588EC059 87.31%)";

  const base =
    isDark && theme
      ? `linear-gradient(180deg,${theme.palette.background.default},#0A0B10)`
      : "linear-gradient(180deg,#FFF,#F5F7FF)";

  return `${overlay},${base}`;
};

const CustomIconButtonConfirm = () =>
  buildIconButtonSx({
    base: { color: "#FFF", backgroundColor: "#E2772E" },
    hover: { backgroundColor: "#CE631A" },
    disabled: { backgroundColor: "#E2772E60" },
  });

const CustomIconButtonLight = (theme?: Theme) => {
  const { surface, surfaceHover, onSurface } = getSurfaceTokens(theme);
  const isDark = theme?.palette.mode === "dark";
  const darkColors = getExamSliderButtonColors();

  return buildIconButtonSx({
    // Dark mode's base/hover reuse the same "light chip" pairing as the
    // unselected ExamSliderButton family -- the plain dark-navy surface
    // color never showed up against the drawer's own dark navy
    // background, whereas this lighter blue-grey with dark text pops the
    // same way it does there.
    base: isDark
      ? { color: darkColors.text, backgroundColor: darkColors.background }
      : { color: onSurface, backgroundColor: surface },
    hover: {
      backgroundColor: isDark ? darkColors.backgroundHover : surfaceHover,
    },
    // Disabled follows ExamSliderButton's own disabled treatment (a faded
    // version of that same background/text) rather than the solid base
    // colors above.
    disabled: isDark
      ? {
          backgroundColor: darkColors.backgroundDisabled,
          color: darkColors.textDisabled,
        }
      : { backgroundColor: "#D6D6D6" },
  });
};

/**
 * IconButton counterpart to ModalCustomConfirmDark -- required `theme` for
 * the same reason as ModalCustomConfirmDark -- see getDarkSolidColors.
 *
 * In dark mode, hover uses surfaceHover instead of getDarkSolidColors' own
 * (barely different) hover shade, so it reads as a clear step up from the
 * page. Light mode keeps getDarkSolidColors' own hover -- surfaceHover
 * there is a near-white gray meant for light-surfaced buttons, and would
 * make this button's white icon disappear against it.
 */
const CustomIconButtonDark = (theme: Theme) => {
  const colors = getDarkSolidColors(theme);
  const { surfaceHover } = getSurfaceTokens(theme);

  return buildIconButtonSx({
    ...colors,
    hover:
      theme.palette.mode === "dark"
        ? { backgroundColor: surfaceHover }
        : colors.hover,
  });
};

const CustomIconButtonGray = () =>
  buildIconButtonSx({
    base: { color: "#FFF", backgroundColor: "#5B6D7A" },
    hover: { backgroundColor: "#475966" },
    disabled: { backgroundColor: "#5B6D7A43" },
  });

const CustomIconButtonError = () =>
  buildIconButtonSx({
    base: { color: "#FFF", backgroundColor: "#E10C0C" },
    hover: { backgroundColor: "#BF0808" },
    disabled: { backgroundColor: "#E10C0C43" },
  });

/**
 * Magenta row-label accent used by the KnowledgeHub "Features" pages
 * (VisualAcuity, General, ContrastSensitivity, ColorVision, VisualField).
 */
const getFeatureRowLabelColor = (theme?: Theme) =>
  theme?.palette.mode === "dark" ? "#E385BE" : "#BD1A7D";

/**
 * Error/"required" red -- e.g. a required-field asterisk, or a form
 * validation message. The plain #E10C0C used everywhere else in the app
 * (buttons, badges) only clears ~3.4:1 contrast against this app's dark-mode
 * surfaces, short of WCAG AA's 4.5:1 for normal text -- #FF8080 clears
 * ~4.5:1+ while still reading clearly as "red"/error.
 */
const getRequiredErrorColor = (theme?: Theme) =>
  theme?.palette.mode === "dark" ? "#FF8080" : "#E10C0C";

/**
 * Selected-card header background, shared by SelectionCard and
 * ComparisonCard so a card looks the same whether it's selected on the
 * selection screen or shown on the comparison screen.
 */
const getSelectedCardHeaderBackground = (theme?: Theme) =>
  theme?.palette.mode === "dark" ? "#44485E" : "#E2772E30";

/**
 * Kills MUI's default hover-darken background on a ButtonBase (IconButton,
 * Radio, ...) used as a plain inline action -- one that should only ever
 * show its icon/color changing, never box itself off with a background.
 * Spread into that control's own sx alongside its other overrides.
 */
const noHoverBackgroundSx = {
  "&.MuiButtonBase-root:hover": {
    backgroundColor: "transparent !important",
  },
};

export {
  getSurfaceTokens,
  getPopoverPaperSx,
  getMenuItemStateColors,
  getFeatureRowLabelColor,
  getRequiredErrorColor,
  getSelectedCardHeaderBackground,
  noHoverBackgroundSx,
  getEmrPageBackground,
  CustomTextFieldProps,
  CustomMultiLineTextFieldProps,
  ModalCustomCancel,
  ModalCustomWarning,
  ModalCustomDelete,
  ModalCustomConfirm,
  ModalCustomConfirmTeal,
  ModalCustomSurfaceLink,
  ModalCustomConfirmDark,
  ModalCustomConfirmDarkCta,
  ExamSliderButton,
  getExamSliderButtonColors,
  ModalCustomConfirmGrey,
  ModalCustomConfirmGreyOutlined,
  ModalCustomConfirmBlack,
  ModalCustomWarningSolid,
  ModalCustomDeleteSolid,
  PageContainerWrapper,
  CustomIconButtonDark,
  CustomIconButtonGray,
  CustomIconButtonLight,
  CustomIconButtonError,
  CustomIconButtonConfirm,
  ModalCustomConfirmLink,
  ModalCustomDarkLink,
};
